import { useState, useCallback } from 'react';
import { Box, IconButton, Typography, CircularProgress, Alert } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import { capturePhoto } from '../../../platform/camera.platform';

// AIDEV-NOTE: Componente de captura e preview de fotos para visitas.
//             Suporta Capacitor Camera (nativo) e input file (PWA fallback).

interface FotoVisitaProps {
  fotos: string[];
  onChange: (fotos: string[]) => void;
  maxFotos?: number;
  disabled?: boolean;
}

export function FotoVisita({ fotos, onChange, maxFotos = 5, disabled = false }: FotoVisitaProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCapturar = useCallback(async () => {
    if (fotos.length >= maxFotos) {
      setError(`Maximo de ${maxFotos} fotos atingido`);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const foto = await capturePhoto();
      onChange([...fotos, foto.webPath ?? foto.path]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao capturar foto';
      if (msg !== 'Nenhuma foto selecionada') {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [fotos, maxFotos, onChange]);

  const handleRemover = useCallback(
    (index: number) => {
      const novasFotos = fotos.filter((_, i) => i !== index);
      onChange(novasFotos);
    },
    [fotos, onChange]
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Fotos da Visita ({fotos.length}/{maxFotos})
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        {fotos.map((foto, index) => (
          <Box key={index} sx={{ position: 'relative', width: 80, height: 80 }}>
            <Box
              component="img"
              src={foto}
              alt={`Foto ${index + 1}`}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}
            />
            {!disabled && (
              <IconButton
                size="small"
                onClick={() => handleRemover(index)}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  bgcolor: 'error.main',
                  color: 'white',
                  width: 20,
                  height: 20,
                  '&:hover': { bgcolor: 'error.dark' }
                }}
              >
                <DeleteIcon sx={{ fontSize: 12 }} />
              </IconButton>
            )}
          </Box>
        ))}

        {!disabled && fotos.length < maxFotos && (
          <IconButton
            onClick={handleCapturar}
            disabled={loading}
            data-testid="foto-visita-btn"
            sx={{
              width: 80,
              height: 80,
              border: '2px dashed',
              borderColor: 'primary.main',
              borderRadius: 1,
              color: 'primary.main'
            }}
          >
            {loading ? <CircularProgress size={24} /> : <CameraAltIcon />}
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
